import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../../components/feature/Navbar";
import Footer from "../../../components/feature/Footer";
import useSEO from "../../../hooks/useSEO";

const ExtremeOptimization = () => {
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  // SEO dinámico
  useSEO({
    title: "Optimización Extrema de Velocidad - Core Web Vitals | SEOgenia",
    description: "Llevamos tu web a velocidades extremas: LCP &lt;1s, FID &lt;50ms, CLS &lt;0.05. Optimización radical de Core Web Vitals que multiplica conversiones y rankings.",
    keywords: "optimización velocidad web, core web vitals, lcp, fid, cls, performance web, velocidad carga",
    canonical: "https://seogenia.es/servicios/optimizacion-extrema",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Optimización Extrema de Velocidad",
      "description": "Optimización radical de Core Web Vitals y velocidad de carga web",
      "provider": {
        "@type": "Organization",
        "name": "SEOgenia",
        "url": "https://seogenia.es"
      },
      "areaServed": "ES",
      "serviceType": "Optimización de Performance Web"
    }
  });

  const faqs = [
    {
      question: "¿Por qué es tan importante la velocidad de carga para mi negocio?",
      answer: "La velocidad impacta directamente en tus ingresos: cada segundo de retraso reduce conversiones un 7%, el 53% de usuarios móviles abandonan si la carga toma más de 3 segundos, y Google penaliza sitios lentos en rankings. Amazon calculó que 100ms de retraso les cuesta 1% de ventas (millones de dólares). Para un e-commerce promedio, mejorar de 5s a 2s puede aumentar conversiones 20-35%."
    },
    {
      question: "¿Qué son exactamente los Core Web Vitals?",
      answer: "Son 3 métricas oficiales de Google que miden experiencia de usuario: LCP (Largest Contentful Paint) - cuánto tarda en cargar el contenido principal (ideal &lt;2.5s), FID (First Input Delay) - cuánto tarda en responder a interacciones (ideal &lt;100ms), y CLS (Cumulative Layout Shift) - cuánto se mueve el contenido mientras carga (ideal &lt;0.1). Desde 2021 son factor de ranking directo en Google."
    },
    {
      question: "¿Cuánto puedo mejorar la velocidad de mi web actual?",
      answer: "Depende del estado inicial, pero típicamente conseguimos: sitios lentos (5-8s) → 1.5-2.5s (mejora 60-70%), sitios medios (3-5s) → 1-1.5s (mejora 50-60%), sitios rápidos (2-3s) → 0.5-1s (mejora 40-50%). Garantizamos Core Web Vitals en verde (LCP &lt;2.5s, FID &lt;100ms, CLS &lt;0.1) o devolvemos tu dinero. Las mejoras más dramáticas vienen de optimizar imágenes, eliminar JavaScript innecesario, y implementar caché inteligente."
    },
    {
      question: "¿La optimización afecta el diseño o funcionalidad de mi web?",
      answer: "No, mantenemos 100% el diseño visual y todas las funcionalidades. Optimizamos el código subyacente, no el aspecto. Técnicas incluyen: comprimir imágenes sin pérdida visual, lazy loading (cargar elementos cuando se necesitan), minificar código, eliminar recursos no utilizados, y optimizar orden de carga. Tu web se ve idéntica pero carga 3-5x más rápido."
    },
    {
      question: "¿Necesito un hosting especial o CDN para velocidad extrema?",
      answer: "No es obligatorio pero ayuda significativamente. Podemos optimizar tu web en tu hosting actual y conseguir mejoras dramáticas. Sin embargo, para velocidad extrema (&lt;1s) recomendamos: hosting con SSD y HTTP/2 (no necesariamente caro), CDN global como Cloudflare (gratis o desde 20€/mes), y caché a nivel servidor. Si tu hosting es muy lento, te recomendamos alternativas mejores sin coste excesivo."
    },
    {
      question: "¿Cuánto tiempo tarda el proceso de optimización?",
      answer: "Para sitios pequeños-medianos (hasta 50 páginas): 1-2 semanas. Para sitios grandes (100-500 páginas): 3-4 semanas. Para sitios muy grandes o complejos (e-commerce con miles de productos): 6-8 semanas. El proceso incluye: auditoría profunda (2-3 días), implementación de optimizaciones (1-3 semanas), testing exhaustivo (3-5 días), y monitoreo post-lanzamiento (1 semana)."
    },
    {
      question: "¿Las mejoras de velocidad son permanentes o necesito mantenimiento?",
      answer: "Las optimizaciones base son permanentes, pero requieren mantenimiento periódico porque: añades nuevo contenido/imágenes que pueden no estar optimizadas, actualizas plugins/temas que pueden añadir código pesado, y Google actualiza sus algoritmos y métricas. Ofrecemos planes de mantenimiento mensual donde monitoreamos Core Web Vitals continuamente y re-optimizamos cuando detectamos degradación."
    },
    {
      question: "¿Puedo medir el ROI de la optimización de velocidad?",
      answer: "Sí, mediante métricas concretas: aumento en tasa de conversión (típicamente 15-35%), reducción en tasa de rebote (típicamente 20-40%), aumento en páginas por sesión (típicamente 10-25%), mejora en rankings de Google (típicamente 5-15 posiciones), y aumento en tráfico orgánico (típicamente 10-30%). Configuramos tracking específico para medir estos KPIs antes y después, calculando el ROI exacto de tu inversión."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <Navbar scrolled={true} />

      {/* Hero Section */}
      <section
        ref={heroRef}
        className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-slate-900"
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center space-x-2 bg-white/10 px-4 py-2 rounded-full shadow-sm mb-6">
              <i className="ri-rocket-line text-pink-400"></i>
              <span className="text-sm font-medium text-white">Máximo Rendimiento</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Optimización Extrema
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 mb-8 leading-relaxed">
              Llevamos tu web al límite absoluto de velocidad y rendimiento. Core Web Vitals perfectos, PageSpeed 100/100 y tiempos de carga menores a 1 segundo.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="#contacto"
                className="px-8 py-3 bg-pink-600 text-white rounded-full font-medium hover:shadow-lg transition-all duration-300 whitespace-nowrap"
              >
                Optimizar al Máximo
              </a>
              <a
                href="#metricas"
                className="px-8 py-3 bg-white/10 text-white rounded-full font-medium hover:shadow-lg transition-all duration-300 border border-white/20 whitespace-nowrap"
              >
                Ver Métricas
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Speed Matters */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                ¿Por qué la Velocidad es Crítica?
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                La velocidad no es solo experiencia de usuario, es dinero. Cada segundo de retraso cuesta conversiones, rankings y credibilidad. Google lo sabe y por eso es un factor de ranking directo.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                En 2025, con Core Web Vitals como factor crítico, una web lenta simplemente no puede competir. Necesitas optimización extrema para destacar.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img
                src="https://public.readdy.ai/ai/img_res/84fdcdb8079bc31b2ae5f4ae69a57b52.jpg"
                alt="Optimización Extrema"
                className="w-full h-auto rounded-2xl shadow-2xl object-cover object-top"
              />
            </motion.div>
          </div>

          {/* Impact Stats */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                stat: "53%",
                label: "Usuarios abandonan si tarda +3s",
                icon: "ri-user-unfollow-line",
                color: "from-red-500 to-pink-500"
              },
              {
                stat: "32%",
                label: "Caída conversión por cada segundo",
                icon: "ri-arrow-down-line",
                color: "from-orange-500 to-red-500"
              },
              {
                stat: "100%",
                label: "Aumento ventas con 0.1s mejora",
                icon: "ri-arrow-up-line",
                color: "from-green-500 to-emerald-500"
              },
              {
                stat: "Top 3",
                label: "Factor de ranking en Google",
                icon: "ri-trophy-line",
                color: "from-cyan-500 to-blue-500"
              }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 rounded-2xl shadow-lg text-center"
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${stat.color} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                  <i className={`${stat.icon} text-2xl text-white`}></i>
                </div>
                <div className="text-4xl font-bold text-gray-900 mb-2">{stat.stat}</div>
                <p className="text-sm text-gray-600">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Web Vitals Section */}
      <section id="metricas" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Core Web Vitals Perfectos
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Las 3 métricas que Google usa para medir la experiencia de usuario
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8 mb-12">
            {[
              {
                metric: "LCP",
                name: "Largest Contentful Paint",
                description: "Mide cuánto tarda en cargar el contenido principal visible",
                target: "&lt; 2.5s",
                icon: "ri-image-line",
                color: "cyan",
                optimizations: [
                  "Optimización de imágenes con WebP/AVIF",
                  "Lazy loading inteligente",
                  "CDN global para assets",
                  "Preload de recursos críticos",
                  "Eliminación de render-blocking"
                ]
              },
              {
                metric: "FID",
                name: "First Input Delay",
                description: "Mide el tiempo hasta que la página responde a la primera interacción",
                target: "&lt; 100ms",
                icon: "ri-cursor-line",
                color: "purple",
                optimizations: [
                  "Code splitting y lazy loading de JS",
                  "Reducción de JavaScript no usado",
                  "Web Workers para tareas pesadas",
                  "Optimización de event listeners",
                  "Defer y async en scripts"
                ]
              },
              {
                metric: "CLS",
                name: "Cumulative Layout Shift",
                description: "Mide la estabilidad visual, evitando saltos inesperados",
                target: "&lt; 0.1",
                icon: "ri-layout-line",
                color: "pink",
                optimizations: [
                  "Dimensiones explícitas en imágenes/videos",
                  "Reserva de espacio para ads y embeds",
                  "Fuentes con font-display: swap",
                  "Evitar inserción dinámica de contenido",
                  "Skeleton screens mientras carga"
                ]
              }
            ].map((vital, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg"
              >
                <div className={`w-16 h-16 bg-gradient-to-br from-${vital.color}-500 to-${vital.color}-600 rounded-2xl flex items-center justify-center mb-6`}>
                  <i className={`${vital.icon} text-3xl text-white`}></i>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">{vital.metric}</div>
                <h3 className="text-lg font-bold text-gray-700 mb-3">{vital.name}</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">{vital.description}</p>
                <div className="inline-block px-4 py-2 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 rounded-full font-bold mb-6">
                  Objetivo: {vital.target}
                </div>
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="font-bold text-gray-900 mb-3 text-sm">Optimizaciones:</h4>
                  <ul className="space-y-2">
                    {vital.optimizations.map((opt, idx) => (
                      <li key={idx} className="flex items-start">
                        <i className="ri-checkbox-circle-fill text-green-500 mt-1 mr-2 flex-shrink-0 text-sm"></i>
                        <span className="text-gray-600 text-sm">{opt}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Optimization Techniques */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Técnicas de Optimización Extrema
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Más de 50 optimizaciones técnicas para máximo rendimiento
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                category: "Optimización de Imágenes",
                icon: "ri-image-2-line",
                techniques: [
                  "Conversión a formatos modernos (WebP, AVIF)",
                  "Compresión inteligente sin pérdida de calidad",
                  "Responsive images con srcset y sizes",
                  "Lazy loading nativo y con Intersection Observer",
                  "Blur-up placeholder para mejor UX",
                  "CDN con optimización automática de imágenes"
                ]
              },
              {
                category: "Optimización de JavaScript",
                icon: "ri-code-s-slash-line",
                techniques: [
                  "Tree shaking para eliminar código muerto",
                  "Code splitting por rutas y componentes",
                  "Minificación y uglificación avanzada",
                  "Compresión Brotli y Gzip",
                  "Defer y async estratégico en scripts",
                  "Eliminación de polyfills innecesarios"
                ]
              },
              {
                category: "Optimización de CSS",
                icon: "ri-palette-line",
                techniques: [
                  "Critical CSS inline en el head",
                  "Purge de CSS no utilizado",
                  "Minificación y compresión",
                  "Eliminación de @import en CSS",
                  "CSS moderno sin prefijos innecesarios",
                  "Atomic CSS para menor tamaño"
                ]
              },
              {
                category: "Optimización de Fuentes",
                icon: "ri-font-size-2",
                techniques: [
                  "Subset de fuentes solo con caracteres usados",
                  "Formatos modernos (WOFF2)",
                  "font-display: swap para evitar FOIT",
                  "Preload de fuentes críticas",
                  "Self-hosting vs Google Fonts optimizado",
                  "Variable fonts cuando sea apropiado"
                ]
              },
              {
                category: "Caché y CDN",
                icon: "ri-database-2-line",
                techniques: [
                  "Cache-Control headers optimizados",
                  "Service Workers para caché avanzado",
                  "CDN global multi-región",
                  "Edge caching para contenido dinámico",
                  "Versionado de assets para cache busting",
                  "Preconnect y DNS-prefetch estratégico"
                ]
              },
              {
                category: "Optimización de Servidor",
                icon: "ri-server-line",
                techniques: [
                  "HTTP/2 o HTTP/3 habilitado",
                  "Compresión Brotli en servidor",
                  "Keep-Alive para conexiones persistentes",
                  "TTFB (Time To First Byte) &lt; 200ms",
                  "Server-side rendering cuando sea apropiado",
                  "Edge computing para lógica cercana al usuario"
                ]
              },
              {
                category: "Optimización de Base de Datos",
                icon: "ri-database-line",
                techniques: [
                  "Índices optimizados en queries frecuentes",
                  "Query optimization y explain analyze",
                  "Connection pooling eficiente",
                  "Caché de queries con Redis/Memcached",
                  "Lazy loading de relaciones",
                  "Paginación eficiente en listados"
                ]
              },
              {
                category: "Optimización de Terceros",
                icon: "ri-links-line",
                techniques: [
                  "Lazy loading de scripts de terceros",
                  "Facade pattern para embeds pesados",
                  "Eliminación de scripts innecesarios",
                  "Self-hosting de analytics cuando sea posible",
                  "Async loading de chat widgets",
                  "Auditoría regular de third-party impact"
                ]
              }
            ].map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg"
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-pink-500 rounded-xl flex items-center justify-center mr-4">
                    <i className={`${section.icon} text-xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{section.category}</h3>
                </div>
                <ul className="space-y-3">
                  {section.techniques.map((technique, idx) => (
                    <li key={idx} className="flex items-start">
                      <i className="ri-arrow-right-s-line text-cyan-600 mt-1 mr-2 flex-shrink-0"></i>
                      <span className="text-gray-600">{technique}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Herramientas de Medición
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Usamos las mejores herramientas para medir y optimizar
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                name: "Google Lighthouse",
                description: "Auditoría completa de rendimiento, SEO y accesibilidad",
                icon: "ri-flashlight-line"
              },
              {
                name: "PageSpeed Insights",
                description: "Datos reales de usuarios (CrUX) y métricas de laboratorio",
                icon: "ri-speed-line"
              },
              {
                name: "WebPageTest",
                description: "Testing avanzado desde múltiples ubicaciones y dispositivos",
                icon: "ri-global-line"
              },
              {
                name: "Chrome DevTools",
                description: "Profiling detallado de rendimiento y network",
                icon: "ri-tools-line"
              },
              {
                name: "GTmetrix",
                description: "Análisis completo con recomendaciones específicas",
                icon: "ri-line-chart-line"
              },
              {
                name: "Pingdom",
                description: "Monitoreo continuo de uptime y velocidad",
                icon: "ri-radar-line"
              },
              {
                name: "Search Console",
                description: "Core Web Vitals reales de tus usuarios",
                icon: "ri-search-line"
              },
              {
                name: "Bundle Analyzer",
                description: "Análisis de tamaño de bundles JavaScript",
                icon: "ri-pie-chart-line"
              }
            ].map((tool, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 rounded-2xl shadow-lg text-center"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <i className={`${tool.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{tool.name}</h3>
                <p className="text-sm text-gray-600">{tool.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Proceso de Optimización
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Metodología sistemática para alcanzar el máximo rendimiento
            </p>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                step: "1",
                title: "Auditoría Inicial Completa",
                description: "Análisis exhaustivo con Lighthouse, PageSpeed, WebPageTest y herramientas custom. Identificamos todos los cuellos de botella.",
                icon: "ri-search-eye-line"
              },
              {
                step: "2",
                title: "Priorización por Impacto",
                description: "Ordenamos optimizaciones por impacto vs esfuerzo. Atacamos primero las que dan mayor mejora con menor trabajo.",
                icon: "ri-sort-desc"
              },
              {
                step: "3",
                title: "Implementación Técnica",
                description: "Aplicamos todas las optimizaciones: imágenes, código, caché, servidor, base de datos y terceros.",
                icon: "ri-code-box-line"
              },
              {
                step: "4",
                title: "Testing y Validación",
                description: "Probamos en múltiples dispositivos, navegadores y conexiones. Validamos que todo funciona perfectamente.",
                icon: "ri-checkbox-multiple-line"
              },
              {
                step: "5",
                title: "Monitoreo Continuo",
                description: "Configuramos alertas y dashboards para monitorear rendimiento 24/7 y detectar regresiones inmediatamente.",
                icon: "ri-dashboard-line"
              }
            ].map((process, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex gap-6 items-start"
              >
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-pink-500 rounded-2xl flex items-center justify-center">
                    <span className="text-2xl font-bold text-white">{process.step}</span>
                  </div>
                </div>
                <div className="flex-1 bg-white p-8 rounded-2xl shadow-lg">
                  <div className="flex items-center mb-3">
                    <i className={`${process.icon} text-2xl text-purple-600 mr-3`}></i>
                    <h3 className="text-2xl font-bold text-gray-900">{process.title}</h3>
                  </div>
                  <p className="text-lg text-gray-600 leading-relaxed">{process.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Resultados Garantizados
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Objetivos que alcanzamos con la optimización extrema
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                metric: "100/100",
                label: "PageSpeed Score",
                description: "Puntuación perfecta en móvil y desktop",
                icon: "ri-medal-line",
                color: "from-yellow-500 to-orange-500"
              },
              {
                metric: "&lt; 1s",
                label: "Tiempo de Carga",
                description: "First Contentful Paint ultrarrápido",
                icon: "ri-timer-flash-line",
                color: "from-green-500 to-emerald-500"
              },
              {
                metric: "Verde",
                label: "Core Web Vitals",
                description: "LCP, FID y CLS en rango óptimo",
                icon: "ri-checkbox-circle-line",
                color: "from-cyan-500 to-blue-500"
              },
              {
                metric: "+150%",
                label: "Mejora Conversión",
                description: "Aumento promedio en conversiones",
                icon: "ri-arrow-up-circle-line",
                color: "from-purple-500 to-pink-500"
              }
            ].map((result, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg text-center"
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${result.color} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                  <i className={`${result.icon} text-3xl text-white`}></i>
                </div>
                <div className="text-4xl font-bold text-gray-900 mb-2">{result.metric}</div>
                <h3 className="text-lg font-bold text-gray-700 mb-2">{result.label}</h3>
                <p className="text-sm text-gray-600">{result.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Velocidad que Convierte
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Empresas que aumentaron conversiones con optimización extrema
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {[
              {
                name: "David Ramírez",
                company: "Shop Online Plus",
                role: "CEO",
                text: "De 6 segundos a 0.8 segundos de carga. Las conversiones aumentaron un 165% y el rebote bajó del 68% al 22%. Inversión recuperada en 3 semanas.",
                rating: 5
              },
              {
                name: "Sofía Navarro",
                company: "Servicios Profesionales",
                role: "Directora Digital",
                text: "PageSpeed 100/100 en móvil y desktop. Google nos subió 12 posiciones en rankings móviles. El tráfico orgánico creció un 89%.",
                rating: 5
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-800 p-8 rounded-2xl border border-gray-700"
              >
                <div className="flex items-center gap-2 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-lg text-white leading-relaxed mb-6 italic">
                  "{testimonial.text}"
                </p>
                <div>
                  <p className="font-bold text-white text-lg">
                    {testimonial.name}
                  </p>
                  <p className="text-gray-300">
                    {testimonial.role} en {testimonial.company}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Preguntas Frecuentes
            </h2>
            <p className="text-lg text-gray-600">
              Resolvemos tus dudas sobre Optimización Extrema
            </p>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300"
              >
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors duration-200"
                >
                  <h3 className="text-lg font-bold text-gray-900 pr-8">
                    {faq.question}
                  </h3>
                  <i
                    className={`ri-arrow-down-s-line text-2xl text-red-600 flex-shrink-0 transition-transform duration-300 ${
                      openFAQ === index ? "rotate-180" : ""
                    }`}
                  ></i>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFAQ === index ? "max-h-[600px]" : "max-h-0"
                  }`}
                >
                  <div className="px-8 pb-6">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-purple-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Lleva tu Web al Máximo Rendimiento
            </h2>
            <p className="text-lg text-white/90 mb-8 leading-relaxed">
              PageSpeed 100/100, Core Web Vitals perfectos y tiempos de carga menores a 1 segundo
            </p>
            <Link
              to="/#contacto"
              className="inline-block px-8 py-4 bg-white text-gray-900 rounded-full font-bold hover:shadow-2xl transition-all duration-300 whitespace-nowrap"
            >
              Solicitar Optimización Extrema
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            ¿Listo para alcanzar la velocidad máxima?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Descubre cómo podemos llevar tu web a puntuaciones perfectas
          </p>
          <Link 
            to="/#contacto"
            className="inline-block px-8 py-4 bg-cyan-500 text-white rounded-full font-medium text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap"
          >
            Solicitar Consulta Gratuita
          </Link>
        </div>
      </section>

      {/* Servicios Relacionados */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Servicios Complementarios
            </h2>
            <p className="text-xl text-gray-600">
              Potencia tu SEO con estos servicios relacionados
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Progressive Web App */}
            <Link 
              to="/servicios/progressive-web-app"
              className="group bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-cyan-400"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-cyan-400 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-smartphone-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-cyan-400 transition-colors">
                Progressive Web App
              </h3>
              <p className="text-gray-600 mb-4">
                Combina optimización extrema con tecnología PWA para ofrecer experiencias ultrarrápidas tipo app nativa.
              </p>
              <div className="flex items-center text-cyan-400 font-medium group-hover:gap-3 transition-all">
                <span>Descubrir más</span>
                <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform"></i>
              </div>
            </Link>

            {/* Arquitectura Semántica */}
            <Link 
              to="/servicios/arquitectura-semantica"
              className="group bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-cyan-400"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-cyan-400 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-layout-grid-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-cyan-400 transition-colors">
                Arquitectura Semántica
              </h3>
              <p className="text-gray-600 mb-4">
                Una arquitectura HTML5 limpia y semántica es la base para lograr optimizaciones extremas de rendimiento.
              </p>
              <div className="flex items-center text-cyan-400 font-medium group-hover:gap-3 transition-all">
                <span>Descubrir más</span>
                <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform"></i>
              </div>
            </Link>

            {/* SEO Agentes IA */}
            <Link 
              to="/servicios/seo-agentes-ia"
              className="group bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-cyan-400"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-cyan-400 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-robot-2-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-cyan-400 transition-colors">
                SEO para Agentes IA
              </h3>
              <p className="text-gray-600 mb-4">
                Los agentes IA priorizan sitios rápidos. Una web optimizada mejora tu ranking en búsquedas conversacionales.
              </p>
              <div className="flex items-center text-cyan-400 font-medium group-hover:gap-3 transition-all">
                <span>Descubrir más</span>
                <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform"></i>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ExtremeOptimization;